import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Download, Printer, Upload } from "lucide-react";
import React, { useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { useStorage } from "./StorageProvider";

interface BackupPanelProps {
  className?: string;
  showPrint?: boolean;
}

export function BackupPanel({ className, showPrint = true }: BackupPanelProps) {
  const { exportBackup, importBackup, printPage } = useStorage();
  const fileRef = useRef<HTMLInputElement>(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      await exportBackup();
      setStatus({ type: "success", message: "Backup downloaded successfully!" });
    } catch (err) {
      setStatus({ type: "error", message: String(err) });
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 4000);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const result = await importBackup(file, { merge: mergeMode });
      if (result.success) {
        setStatus({
          type: "success",
          message: `Restored ${result.tablesRestored.length} table(s) successfully.`,
        });
      } else {
        setStatus({
          type: "error",
          message: result.errors.join(", "),
        });
      }
    } catch (err) {
      setStatus({ type: "error", message: String(err) });
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 5000);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-wrap gap-2">
        {/* Export */}
        <button
          onClick={handleExport}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Download size={13} />
          Export Backup
        </button>

        {/* Import trigger */}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-foreground text-xs font-medium hover:bg-accent transition-colors disabled:opacity-50"
        >
          <Upload size={13} />
          Import Backup
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleImport}
        />

        {/* Print */}
        {showPrint && (
          <button
            onClick={printPage}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-foreground text-xs font-medium hover:bg-accent transition-colors no-print"
          >
            <Printer size={13} />
            Print / PDF
          </button>
        )}
      </div>

      {/* Merge toggle */}
      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer w-fit">
        <div
          onClick={() => setMergeMode((v) => !v)}
          className={cn(
            "relative w-8 h-4 rounded-full transition-colors cursor-pointer",
            mergeMode ? "bg-primary" : "bg-muted border border-border"
          )}
        >
          <motion.div
            animate={{ x: mergeMode ? 16 : 2 }}
            transition={{ type: "spring", bounce: 0.25, duration: 0.3 }}
            className="absolute top-0.5 w-3 h-3 rounded-full bg-white"
          />
        </div>
        Merge mode (combine instead of overwrite)
      </label>

      {/* Status toast */}
      <AnimatePresence>
        {status && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className={cn(
              "flex items-start gap-2 p-2.5 rounded-lg text-xs border",
              status.type === "success" &&
                "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
              status.type === "error" &&
                "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
            )}
          >
            {status.type === "success" ? (
              <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
            ) : (
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
            )}
            {status.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
